import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';

// ---------------------------------------------------------------------------
// Outreach Job — one scheduled outreach action on any channel (a LinkedIn invite
// or DM today; an email when that channel lands). ADR-0002: the TYPE is
// channel-neutral, but the runtime identity is kept to avoid a live-collection
// migration — _class stays 'LinkedinJob', the collection stays 'linkedin_jobs', and
// the sending-account field keeps its 'linkedinAccountId' name (baseql maps a
// property straight to its DB column, so that rename is a data migration, tracked
// as a known gap).
// ---------------------------------------------------------------------------

@Entity()
export class OutreachJob extends MongoEntity {
  static override _class = 'LinkedinJob';
  static override _collectionName = 'linkedin_jobs';

  @Field(type => String) campaignId!: string;
  @Field(type => String) userId!: string;
  @Field(type => String) leadId!: string;
  @Field(type => String) linkedinAccountId!: string;

  /** The sequence step index (0-based) this job corresponds to */
  @Field(type => Int) stepIndex!: number;
  @Field(type => String) stepId!: string;
  /** 'linkedin_connect' | 'linkedin_dm' */
  @Field(type => String) channel!: string;

  /** The pre-generated message to send */
  @Field(type => String) message!: string;

  /**
   * Email subject line. Set only for email channels — LinkedIn carries its
   * whole message in `message` and has no subject. A threaded follow-up is sent
   * with an `Re:` prefix at send time; this holds the step's own subject.
   */
  @Field(type => String) subject?: string;

  /** For 'replied' condition steps: the un-interpolated prompt template (message generated at execution time) */
  @Field(type => String) promptTemplate?: string;

  /** Sequence step condition: 'always' | 'accepted' | 'not_accepted' | 'no_reply' | 'replied' */
  @Field(type => String) condition?: string;

  /** 'pending' | 'ready' | 'running' | 'completed' | 'failed' | 'skipped' | 'cancelled' | 'simulated' */
  @Field(type => String) status!: string;

  /** ISO timestamp — earliest time this job may execute */
  @Field(type => String) scheduledAt!: string;
  /** ISO timestamp — when the job actually executed */
  @Field(type => String) executedAt?: string;

  /**
   * Did the invitation that landed carry its personal note?
   *
   * Only ever written for a completed `linkedin_connect` job, and never
   * inferred. The note policy is per account and per month, so asking it later
   * answers "would we attach one today", not "did this lead receive one" — and
   * the lead history needs the second question. Until this field existed that
   * history rendered the generated note with a "Sent" badge for invitations
   * that had gone out bare, which is the note fix turning a once-true screen
   * into a false one.
   *
   * Absent on every job written before 16 August 2026. Absence means unknown
   * and must not be read as either answer.
   */
  @Field(type => Boolean) noteCarried?: boolean;
  /**
   * The Unipile invitation id returned when THIS job sent its connection
   * request (`{"object":"UserInvitationSent","invitation_id":"…"}`). Written
   * only for a completed `linkedin_connect` job, and it is the exact, per-lead
   * anchor for withdrawing that invitation later — cancelling a deleted
   * campaign's invites recalls only the ids its own jobs recorded, never one an
   * earlier or parallel campaign sent, and never a relationship we did not
   * create. Absent on jobs completed before this field existed and on any
   * invite whose response carried no id; those simply cannot be recalled.
   */
  @Field(type => String) invitationId?: string;
  /**
   * ISO timestamp — when a tick claimed this job as 'running'. A lease. A job
   * still 'running' long after this was stamped was stranded by a worker that
   * died mid-pass (a QA redeploy, a crash), and the next tick reclaims it —
   * without it, selection only ever looks at 'pending'/'ready' and the job is
   * invisible forever. Only meaningful while status is 'running'.
   */
  @Field(type => String) runningAt?: string;
  /** Error message if failed */
  @Field(type => String) errorMessage?: string;
  /**
   * Why this job is parked, when it is parked for a legitimate reason rather
   * than a failure — e.g. a DM waiting for the lead to accept the connection
   * request, which can last days and is the sequence working as intended.
   *
   * Kept apart from `errorMessage` deliberately: the two were the same field,
   * so "waiting" and "broken" were indistinguishable, and a normal wait
   * surfaced to the operator as "We're experiencing issues with LinkedIn".
   * Mutually exclusive — whichever is set, the other is cleared.
   */
  @Field(type => String) waitingReason?: string;
  /**
   * The classified reason this job is stuck or was parked — 'rate_limited',
   * 'auth_or_permission', 'not_found', 'provider_error', 'unknown'.
   *
   * Derived from the HTTP status the provider returned, never from matching its
   * prose: the status is what LinkedIn commits to, and a guess read out of an
   * error string sends somebody to fix the wrong thing. This is what lets the
   * operator banner say what is actually wrong instead of "issues with
   * LinkedIn"; `errorMessage` keeps the full text for the log.
   */
  @Field(type => String) failureKind?: string;
  /** The HTTP status LinkedIn returned, kept so nothing has to re-parse the text. */
  @Field(type => Int) failureStatus?: number;
  /**
   * The provider's answer, VERBATIM, whenever it refused something.
   *
   * Not a classification, not a summary, not our wording: the bytes Unipile
   * sent back. It exists because on 14 August the campaign had been refused
   * ~80 times a day for ten days and the literal text of that refusal was
   * nowhere a person on this project could reach — it went to a log nobody
   * here has access to, and the job recorded `errorMessage: null` because a
   * refusal is handled as a wait rather than a failure.
   *
   * Two hours went into inferring what LinkedIn was saying. This field is so
   * that never has to happen again: whatever the provider says, it is on the
   * job, and the diagnostics route shows it.
   */
  @Field(type => String) providerMessage?: string;
  /**
   * Why this job was closed without running — the lead replied, finished, or
   * went on the Do Not Contact list.
   *
   * Its own field because the closing reason used to be written over
   * `errorMessage`, and on 17 August that erased the verbatim provider refusal
   * from two jobs that had already been refused: the closing sentence and the
   * diagnosis are both true, and only the diagnosis is expensive to recover.
   * `errorMessage` is now written only where a job has none, so a job closed
   * without ever failing still says why (helpers/job-skip.ts).
   */
  @Field(type => String) skipReason?: string;
  /**
   * When the operator's manual probe last LOOKED AT this job.
   *
   * It is the rotation key: probes are handed to the least-recently-probed job
   * first, and a missing value sorts ahead of any timestamp, so a lead nobody
   * has tried goes before one already tried. Without it the probe returned the
   * same job on every call — five probes, one lead, the same answer five times.
   *
   * Attention, deliberately, not delivery. It is stamped when the probe picks
   * the job, before the profile is even read, because every way a probe can end
   * early is still a probe the operator has spent — and a rotation key written
   * only on success rotates only on success. That is how four probes aimed at
   * four accounts all came back from one.
   */
  @Field(type => String) lastProbedAt?: string;
  /**
   * How many times LinkedIn has refused this invitation as "already invited
   * recently". Drives an escalating retry gap.
   *
   * Deliberately NOT `retries`: that counts failures, and a refusal is not a
   * failure. Nor a generic deferral counter — business hours postpone jobs
   * every single evening, so counting those would push the gap to its maximum
   * on day one and defeat the escalation entirely.
   */
  @Field(type => Int) inviteRefusals?: number;
  /** Number of retry attempts */
  @Field(type => Int) retries!: number;

  /**
   * Set when this job delivers an approved ReplyDraft (vs. a regular sequence
   * step). Lets the tick worker (a) exempt it from the "lead already replied"
   * gate — that gate exists to halt outbound SEQUENCE steps once a lead
   * replies, but this job's entire purpose IS to reply to that reply — and
   * (b) reconcile the draft's displayed status once the job actually resolves.
   */
  @Field(type => String) replyDraftId?: string;

  /**
   * When a person pressed "Send DM now" for this job.
   *
   * The manual send does not deliver anything itself — it puts the job back to
   * `pending` and the scheduler picks it up (routes/campaign-leads.ts). So
   * without this marker the automatic-DM rule would skip the manual send too:
   * the button would report success, the job would be skipped a second later,
   * and nothing would reach anybody (helpers/dm-eligibility.ts).
   *
   * It is also worth keeping on its own. "This message went out because a human
   * decided it should" is not derivable afterwards from anything else here.
   */
  @Field(type => String) manuallyRequestedAt?: string;

  @Field(type => String) createdAt!: string;
}

// ---------------------------------------------------------------------------
// LinkedIn Account Daily Stats — Tracks daily usage per account for limits
// ---------------------------------------------------------------------------

@Entity()
export class LinkedinDailyStats extends MongoEntity {
  static override _class = 'LinkedinDailyStats';
  static override _collectionName = 'linkedin_daily_stats';

  @Field(type => String) linkedinAccountId!: string;
  /** Date string YYYY-MM-DD */
  @Field(type => String) date!: string;

  /**
   * EVERY request this application made through this account today — reads
   * included, successes and failures alike.
   *
   * The outreach counters below describe what we intended to do. This one
   * describes what the provider actually saw, which is the thing it throttles
   * on. Both are needed: an account can be well within its invitation budget
   * while making thousands of profile requests an hour, and that is precisely
   * the state that produced an outage nobody could name.
   */
  @Field(type => Int) providerRequests!: number;
  /**
   * The read subset of the above. Broken out because reads are the cheapest
   * thing to make accidentally and the easiest to make in a loop.
   */
  @Field(type => Int) providerReads!: number;

  @Field(type => Int) invitesSent!: number;
  /**
   * Invitations LinkedIn REFUSED today — attempted, transmitted, declined.
   *
   * Kept apart from `invitesSent` because they are different facts: one is
   * "this many people got an invitation", the other is "this many requests
   * this account fired at LinkedIn and got turned away". The first is a
   * campaign result; the second is what LinkedIn sees when it decides whether
   * this account is behaving like a person.
   *
   * The daily limit is checked against the SUM. A refused attempt costs an API
   * call and is visible to LinkedIn exactly like a successful one, so leaving
   * it out of the budget means an account with 20 sends and 8 blocked retries
   * has actually made 28 requests while the limit believes it made 20 — which
   * is how accounts get restricted.
   */
  @Field(type => Int) invitesRefused?: number;
  @Field(type => Int) dmsSent!: number;
  @Field(type => Int) totalActions!: number;
  @Field(type => Int) errorsHit!: number;
  /** Number of 429 / rate-limit responses */
  @Field(type => Int) rateLimitsHit!: number;

  /** If true, this account is paused for the rest of the day */
  @Field(type => Boolean) circuitBroken!: boolean;

  /**
   * Invitations sent by the operator's manual probe, which deliberately
   * bypasses the daily budget.
   *
   * Counted apart from `invitesSent` for two reasons: the bypass needs a
   * ceiling of its own, and a day's numbers should still say how they were
   * reached. A probe is a person deciding to spend one attempt to learn
   * something; that is not the same event as the scheduler doing its work.
   */
  @Field(type => Int) operatorProbes?: number;
}

// ---------------------------------------------------------------------------
// LinkedIn Account Warmup — Tracks warmup phase per account
// ---------------------------------------------------------------------------

@Entity()
export class LinkedinAccountWarmup extends MongoEntity {
  static override _class = 'LinkedinAccountWarmup';
  static override _collectionName = 'linkedin_account_warmup';

  @Field(type => String) linkedinAccountId!: string;
  @Field(type => String) userId!: string;
  /** ISO timestamp — when this account started being used for outreach */
  @Field(type => String) warmupStartedAt!: string;

  /** Number of actions performed since warmup start */
  @Field(type => Int) totalActionsSinceStart!: number;
}

/**
 * One refused invitation, kept so the NEXT argument about what a refusal means
 * can be settled with data instead of inference.
 *
 * Verbatim body included, truncated only for size: every correct reading of
 * this refusal so far came from the provider's own `detail`, and every wrong
 * one came from the slug or from a body that had been cut short before the
 * detail was reached.
 */
@Entity()
export class RefusedInvitation extends MongoEntity {
  static override _class = 'RefusedInvitation';

  @Field(type => String) leadId!: string;
  @Field(type => String) campaignId?: string;
  /** The LinkedIn profile URL, because a lead id means nothing to a reader. */
  @Field(type => String) linkedinUrl?: string;
  @Field(type => String) refusedAt!: string;
  /** `HTTP <status> <body>`, exactly as the provider sent it. */
  @Field(type => String) providerMessage?: string;
}

// ---------------------------------------------------------------------------
// LinkedIn Invite Cooldown — the account is what LinkedIn throttles
//
// One row per sending account, and it exists because the thing being refused is
// not the lead. On the live campaign 76 leads carried `connectionState:
// no_invite_sent` and `connectionSentAt: null` — nobody had ever been invited —
// while LinkedIn answered `already_invited_recently` to every attempt. A
// refusal about a person we never contacted is not about that person.
//
// Kept in its own collection rather than on the daily-stats row because a
// cooldown crosses midnight and a daily row does not. Reading a cooldown out of
// two days' rows is the kind of arithmetic that quietly gets one boundary
// wrong.
// ---------------------------------------------------------------------------

@Entity()
export class LinkedinInviteCooldown extends MongoEntity {
  static override _class = 'LinkedinInviteCooldown';
  static override _collectionName = 'linkedin_invite_cooldown';

  @Field(type => String) linkedinAccountId!: string;

  /**
   * ISO — no invitation may be ATTEMPTED from this account before this moment.
   *
   * Absent (via `$unset`, never null) means the account is free. Absence is the
   * normal state, so the gate's fast path is "no row, or no field".
   */
  @Field(type => String) cooldownUntil?: string;

  /**
   * Refusals in a row, cleared by the first invitation that lands. Picks the rung.
   *
   * Deliberately not "refusals today": the question the ladder answers is "how
   * long has this account been unable to invite", which a daily counter cannot
   * express.
   */
  @Field(type => Int) consecutiveRefusals?: number;

  /** Why, in the words the campaign view shows the operator. */
  @Field(type => String) reason?: string;

  @Field(type => String) lastRefusalAt?: string;
  /** When an invitation last got through — the moment the cooldown was cleared. */
  @Field(type => String) lastSuccessAt?: string;
  /** When the current single probing attempt was let through. */
  @Field(type => String) probeStartedAt?: string;

  /**
   * WHO was refused, most recent first — the one fact that separates the two
   * readings of this refusal, and the one nobody was writing down.
   *
   * The account reading above is right for what it was measured against: 76
   * DIFFERENT people, none of them ever contacted, all refused. That is an
   * account. But on 19 August the same slug came back for a single lead who
   * genuinely had been invited days earlier by an earlier test campaign, with
   * the provider's own detail reading "An invitation has already been sent
   * recently to this recipient" — and holding the whole account for 24 hours
   * parked an unrelated lead in an unrelated campaign.
   *
   * Both readings fit the slug. What tells them apart is the SPREAD: many
   * distinct recipients means the account, one recipient again and again means
   * the recipient. Neither incident could answer that, because the refused lead
   * was never recorded — so the argument was settled twice by inference and
   * twice at the cost of days.
   *
   * Bounded to the most recent few. This is evidence for a decision, not a log.
   */
  @Field(type => [RefusedInvitation]) recentRefusals?: RefusedInvitation[];
}

// ---------------------------------------------------------------------------
// LinkedIn Note Ration — how many personalised invitations this account has left
//
// LinkedIn rations invitations that carry a personal note. Free accounts get
// five a month (LinkedIn's own Help Center figure, 200 characters); a paid seat
// reports no monthly cap. Invitations WITHOUT a note are governed by an
// entirely separate and far larger allowance, which is the one the weekly cap
// in linkedin-safety.ts tracks.
//
// Measured on 15 August, and the measurement is the reason this row exists:
// `LH0XdylWT6mXWxTIj9ol2g` — a free account (`premiumFeatures: []`) with no
// note-carrying invitation that month — sent two, back to back, both 201. The
// campaign's four senders, equally free, were refused every time. The
// difference was not the tier. It was that the campaign had already spent its
// ration in the first days of the month.
//
// One row per account, keyed by MONTH rather than by a count, because a count
// is not what we can observe. We never see "three of five used"; we see one
// refusal, and from that moment the answer for the rest of the month is no.
// The month is the whole state.
// ---------------------------------------------------------------------------

@Entity()
export class LinkedinNoteRation extends MongoEntity {
  static override _class = 'LinkedinNoteRation';
  static override _collectionName = 'linkedin_note_ration';

  @Field(type => String) linkedinAccountId!: string;

  /**
   * `YYYY-MM` (UTC) of the month whose ration is spent.
   *
   * Absent (via `$unset`, never null) would mean "no note has been refused",
   * but nothing writes that state: the row is created ALREADY spent the first
   * time an account is seen. See `noteAllowedFor` for why absence of a row must
   * never be read as permission.
   *
   * Compared against the current month rather than counted down, so the ration
   * returns at the month boundary with nothing scheduled and nobody flipping a
   * switch.
   */
  @Field(type => String) spentForMonth?: string;

  /** When the ration was marked spent — the refusal that taught us, or the seed. */
  @Field(type => String) spentAt?: string;

  /**
   * `connection_params.im.premiumFeatures` as the provider reported it.
   *
   * A non-empty list means a paid seat and therefore no monthly ration to
   * track. Read from the provider, never inferred, and stored so the decision
   * does not need a live call on every invitation.
   *
   * NOT `premiumId`: that field reads `null` on accounts whose features list
   * `['recruiter']`, so a check on it calls a Recruiter seat free.
   */
  @Field(type => [String]) premiumFeatures?: string[];

  /** When the tier above was last read from the provider. */
  @Field(type => String) premiumCheckedAt?: string;
}
